import React, { FunctionComponent, useRef } from "react";
import { IEstimatedVolumeCount } from "../../../../Models/Models";
import { getPrettyNumber } from "../../../Common";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import {
    RenderType,
    DigitalShareItemType,
    DigitalShareAppType,
} from "../../../../Enums/Enums";
import {
    colorTwoBright,
    colorTwoDark,
    anotherLightGray,
} from "../../../../globalStyles";
import { StylishMan } from "./GPSRelVolMetStyles";
import { GPSRelVolMetOutput } from "./GPSRelVolMetOutput";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";

interface IGPSRelVolMet {
    location1Name: string;
    location2Name?: string;
    buds: number;
    region: string;
    storeEstVol: IEstimatedVolumeCount;
    benchmarkEstVol?: IEstimatedVolumeCount;
    visitsCount?: number;
    benchmarkVisitsCount?: number;
    renderType?: RenderType;
    app?: DigitalShareAppType;
    itemType?: DigitalShareItemType;
    shareText?: string;
}

export const GPSRelVolMet: FunctionComponent<IGPSRelVolMet> = (
    props: IGPSRelVolMet
) => {
    const { location1Name, location2Name, buds, region, visitsCount, storeEstVol, benchmarkEstVol, benchmarkVisitsCount } = props;
    const viewBoxVal = "165 160 190 190";
    const calcPctOfOther = (val1: number, val2: number): string => {
        return getPrettyNumber((val1 / val2) * 100, 0);
    };

    const calcPctOfTotal = (val1: number, val2: number): number => {
        return (val1 / (val1 + val2)) * 100;
    };

    const calcManIndVal = (totalPeeps: number): number => {
        return 100 / totalPeeps;
    };

    const findAverageDailyVolume = (budsCurrent: number, regionId: string): number => {
        let dailyVolume;
        switch (budsCurrent && regionId) {
            case 1 && 'NE': dailyVolume = 10000;
                break;
            case 1 && 'WP': dailyVolume = 28000;
                break;
            case 1 && 'WSC': dailyVolume = 101000;
                break;
            case 1 && 'WM': dailyVolume = 43000;
                break;
            case 1 && 'ESC': dailyVolume = 61000;
                break;
            case 1 && 'SA': dailyVolume = 106000;
                break;
            case 1 && 'WNC': dailyVolume = 70000;
                break;
            case 1 && 'ENC': dailyVolume = 103000;
                break;
            case 1 && 'MA': dailyVolume = 25000;
                break;
            case 2 && 'WSC': dailyVolume = 206000;
                break;
            case 2 && 'WNC': dailyVolume = 109000;
                break;
            case 2 && 'ENC': dailyVolume = 274000;
                break;
            case 2 && 'SA': dailyVolume = 376000;
                break;
            case 2 && 'MA': dailyVolume = 107000;
                break;
            case 2 && 'WP': dailyVolume = 78000;
                break;
            case 2 && 'ESC': dailyVolume = 129000;
                break;
            case 2 && 'NE': dailyVolume = 49000;
                break;
            case 2 && 'WM': dailyVolume = 81000;
                break;
            case 3 && 'WNC': dailyVolume = 85000;
                break;
            case 3 && 'MA': dailyVolume = 91000;
                break;
            case 3 && 'ENC': dailyVolume = 223000;
                break;
            case 3 && 'WM': dailyVolume = 84000;
                break;
            case 3 && 'SA': dailyVolume = 284000;
                break;
            case 3 && 'WSC': dailyVolume = 178000;
                break;
            case 3 && 'ESC': dailyVolume = 60000;
                break;
            case 3 && 'NE': dailyVolume = 38000;
                break;
            case 3 && 'WP': dailyVolume = 107000;
                break;
            case 4 && 'MA': dailyVolume = 44000;
                break;
            case 4 && 'WP': dailyVolume = 72000;
                break;
            case 4 && 'ESC': dailyVolume = 14000;
                break;
            case 4 && 'ENC': dailyVolume = 79000;
                break;
            case 4 && 'WSC': dailyVolume = 60000;
                break;
            case 4 && 'WM': dailyVolume = 49000;
                break;
            case 4 && 'NE': dailyVolume = 13000;
                break;
            case 4 && 'SA': dailyVolume = 99000;
                break;
            case 4 && 'WNC': dailyVolume = 28000;
                break;
            case 5 && 'ENC': dailyVolume = 15000;
                break;
            case 5 && 'NE': dailyVolume = 5000;
                break;
            case 5 && 'WSC': dailyVolume = 11000;
                break;
            case 5 && 'MA': dailyVolume = 38000;
                break;
            case 5 && 'WP': dailyVolume = 30000;
                break;
            case 5 && 'WNC': dailyVolume = 4000;
                break;
            case 5 && 'WM': dailyVolume = 9000;
                break;
            case 5 && 'SA': dailyVolume = 16000;
                break;
            case 6 && 'MA': dailyVolume = 12000;
                break;
            case 6 && 'NE': dailyVolume = 1000;
                break;
            case 6 && 'WP': dailyVolume = 2000;
                break;
            case 6 && 'SA': dailyVolume = 1000;
                break;
            case 6 && 'ENC': dailyVolume = 3000;
                break;
            default: dailyVolume = 0;
                break;
        }
        return dailyVolume;
    };
    const storePctOfTotal = benchmarkEstVol
        ? calcPctOfTotal(
            visitsCount,
            benchmarkVisitsCount
        ) : calcPctOfTotal(
            visitsCount,
            findAverageDailyVolume(buds, region)
        );

    const benchPctOfTotal = benchmarkEstVol ? calcPctOfTotal(
        benchmarkVisitsCount,
        visitsCount
    ) : 0;


    const loc1PctOfOther = benchmarkVisitsCount ? calcPctOfOther(visitsCount, benchmarkVisitsCount) : "";
    const loc2PctOfOther = benchmarkVisitsCount ? calcPctOfOther(benchmarkVisitsCount, visitsCount) : "";

    const setPeeps = (
        rows: number,
        peepsInRow: number,
        pctTotalCompare: number,
        color: string,
        type: string
    ): any[] => {
        const peeps = [];
        for (let rowIdx = 1; rowIdx <= rows; rowIdx++) {
            peeps.push([]);
            for (
                let index = 1 + peepsInRow * (rowIdx - 1);
                index <= peepsInRow * rowIdx;
                index++
            ) {
                peeps[rowIdx - 1].push(
                    <StylishMan
                        key={`${type}PeepsRow${rowIdx}-${index}`}
                        viewBox={viewBoxVal}
                        style={{
                            color:
                                index * calcManIndVal(rows * peepsInRow) <= pctTotalCompare
                                    ? color
                                    : anotherLightGray,
                        }}
                    />
                );
            }
        }
        return peeps;
    };

    const relVolMetRef = useRef();

    // This regexp adds commas where appropriate to the value provided.
    const loc1Vol = (storeEstVol.visits * visitsCount).toLocaleString();
    const loc2Vol = benchmarkEstVol ? (benchmarkEstVol.visits * visitsCount).toLocaleString() : "";

    const loc1PctFinal = featureFlags.Mobilytics.newRelativeVolume
        ? storePctOfTotal.toString()
        : loc1PctOfOther;

    const loc2PctFinal = featureFlags.Mobilytics.newRelativeVolume
        ? benchPctOfTotal.toString()
        : loc2PctOfOther;

    if (
        props.renderType === RenderType.DIGITAL_SHARE
        && isOutputSelected(props.app, DigitalShareItemType.RelativeVolume)
    ) {
        registerRef(props.app, DigitalShareItemType.RelativeVolume, relVolMetRef);

        const shareStorePeeps = setPeeps(
            6,
            12,
            storePctOfTotal,
            colorTwoBright,
            "shareStore"
        );
        const shareBenchPeeps = setPeeps(
            6,
            12,
            benchPctOfTotal,
            colorTwoDark,
            "shareBench"
        );
        registerElement(
            props.app,
            DigitalShareItemType.RelativeVolume,
            "",
            <GPSRelVolMetOutput
                location1Name={location1Name}
                location2Name={location2Name}
                location1Color={colorTwoBright}
                location2Color={colorTwoDark}
                loc1Peeps={shareStorePeeps}
                loc2Peeps={shareBenchPeeps}
                loc1Vol={loc1Vol}
                loc2Vol={loc2Vol}
                loc1CalcPctOfLoc2={loc1PctFinal}
                loc2CalcPctOfLoc1={loc2PctFinal}
            />
        );
    }

    const storePeeps = benchmarkVisitsCount
        ? setPeeps(3, 18, storePctOfTotal, colorTwoBright, "store")
        : setPeeps(3, 36, storePctOfTotal, colorTwoBright, "store");
    const benchPeeps = benchmarkVisitsCount ? setPeeps(3, 18, benchPctOfTotal, colorTwoDark, "bench") : [];


    return (
        <GPSRelVolMetOutput
            location1Name={location1Name}
            location2Name={location2Name}
            location1Color={colorTwoBright}
            location2Color={colorTwoDark}
            loc1Peeps={storePeeps}
            loc2Peeps={benchPeeps}
            loc1Vol={loc1Vol}
            loc2Vol={loc2Vol}
            loc1CalcPctOfLoc2={loc1PctFinal}
            loc2CalcPctOfLoc1={loc2PctFinal}
            rh4Title="Relative Volume Metrics"
        />
    );
};
